import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { LoadingSpinner } from "../../../../components/shared/LoadingSpinner";
import { Employee, EmployeeAccount, Transaction } from "../../../../models";
import { formatCurrency, formatPercentage, formatDateForQuery } from "../../../../utils/formatters";
import { extraordinaryColors } from "../../../../utils/theme";
import { useData, useCustomQuery } from "../../../../utils/useData";
import { useState } from "react";
import { TransactionsTable } from "../../../../components/table/Transactions/TransactionsTable";
import { convertTransactionsToCsv, convertTransactionsToTaxLabFbtCsv, downloadCsv } from "../../../../utils/convertToCSV";
import { TransactionsTableControlBar } from "../../../../components/table/Transactions/TransactionsTableControlBar";
import { TransactionStatus } from "../../../../models/transactions/transactionStatus";
import { TransactionType } from "../../../../models/transactions/transactionType";
import { useSearchParams } from "react-router-dom";

// Helper function to format date as YYYY-MM-DD
const formatDateOnly = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

// Helper function to parse YYYY-MM-DD to Date
const parseDateOnly = (dateStr: string): Date | undefined => {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
};

// Helper function to get default start date (30 days ago at midnight UTC)
const getDefaultStartDate = (): Date => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 30);
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0
    ));
};

const TotalDisplayCard = styled(Box)({
  width: "100%",
  backgroundColor: extraordinaryColors.green.light,
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 24,
  paddingBottom: 24,
  borderRadius: 10
});

const StatisticTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 500
});

const StatisticDisplay = styled(Typography)({
  fontSize: 28,
  fontWeight: 700
});

const StatisticSubtitle = styled(Typography)({
  fontSize: 12
});

const EmployeeOverview = ({
  employee,
  employeeAccounts
}: {
  employee: Employee;
  employeeAccounts: EmployeeAccount[];
}) => {
  const { ledgers, allAccounts, employer, allEmployees, accounts, serviceSectors, tagCategories, allPlans } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Initialize dates from URL parameters or default values
  const initialStartDate = searchParams.get('fromDate') 
      ? parseDateOnly(searchParams.get('fromDate')!) ?? getDefaultStartDate()
      : getDefaultStartDate();
  const initialEndDate = searchParams.get('toDate') 
      ? parseDateOnly(searchParams.get('toDate')!) 
      : undefined;
  
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('searchTerm') || '');
  const [selectedPlans, setSelectedPlans] = useState<string[]>(
      searchParams.get('planId')?.split(',').filter(Boolean) || []
  );
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(
      searchParams.get('accountId')?.split(',').filter(Boolean) || []
  );

  // Build query string with filters
  const queryString = [
      `assumeLocalTime=true`,
      `employerId=${employer.data?.id}`,
      `employeeId=${employee.id}`,
      `page=${page}`,
      `pageSize=${pageSize}`,
      `status=${TransactionStatus.PENDING}`,
      `status=${TransactionStatus.APPROVED}`,
      `type=${TransactionType.PURCHASE}`,
      `type=${TransactionType.REFUND}`,
      `type=${TransactionType.REIMBURSEMENT}`,
      startDate ? `fromDate=${formatDateForQuery(startDate)}` : '',
      endDate ? `toDate=${formatDateForQuery(endDate)}` : '',
      searchTerm ? `searchTerm=${searchTerm}` : '',
      ...selectedPlans.map(planId => `planId=${planId}`),
      ...selectedAccounts.map(accountId => `accountId=${accountId}`)
  ].filter(Boolean).join('&');

  // Fetch transactions with pagination
  const transactions = useCustomQuery<{
      items: Transaction[];
      totalItems: number;
  }>({
      endpoint: `/transactions?${queryString}`,
      options: {
          enabled: !!employer.data?.id
      }
  });

  // Add export query
  const exportQueryString = [
      `employerId=${employer.data?.id}`,
      `employeeId=${employee.id}`,
      `status=${TransactionStatus.PENDING}`,
      `status=${TransactionStatus.APPROVED}`,
      `status=${TransactionStatus.DECLINED}`,
      `type=${TransactionType.PURCHASE}`,
      `type=${TransactionType.REFUND}`,
      `type=${TransactionType.REIMBURSEMENT}`,
      startDate ? `fromDate=${formatDateForQuery(startDate)}` : '',
      endDate ? `toDate=${formatDateForQuery(endDate)}` : '',
      searchTerm ? `searchTerm=${searchTerm}` : '',
      ...selectedPlans.map(planId => `planId=${planId}`),
      ...selectedAccounts.map(accountId => `accountId=${accountId}`)
  ].filter(Boolean).join('&');

  const exportTransactions = useCustomQuery<Transaction[]>({
      endpoint: `/transactions/export?${exportQueryString}`,
      options: {
          enabled: false // Only fetch when needed
      }
  });

  if (
    ledgers.isLoading ||
    ledgers.isIdle ||
    allAccounts.isLoading ||
    allAccounts.isIdle
  )
    return <LoadingSpinner />;

  var totalSpend = 0;
  var totalTxns = 0;

  ledgers.data?.items.forEach((ledger) => {
    if (ledger.employeeId !== employee.id) return;
    totalSpend += ledger.billingAmount.amount;
    totalTxns++;
  });

  // If the account has been deleted, then the total contribution doesn't reflect the amount they employee was able to spend, as they could have remaining balance.
  // So we need to subtract the remaining balance to see how much was "actually" contributed. This will result in the deleted accounts utilization always being 100%
  const totalEmployeeContribution: number =
    allAccounts.data?.items
      .filter((a) => a.employeeId === employee.id)
      .reduce((accumulator, account): number => {
        if (account.deletedAt) {
          return (accumulator +=
            account.totalContributionAmount.amount -
            account.availableBalance.amount);
        }
        return (accumulator += account.totalContributionAmount.amount);
      }, 0) ?? 0;

  const totalRemainingBalance: number =
    employeeAccounts.reduce((accumulator, account): number => {
      return (accumulator += account.availableBalance.amount);
    }, 0) ?? 0;

  const averageTxn = totalSpend / totalTxns;
  const remainingBalance = totalRemainingBalance;
  const utilizationPercentage = Math.min(
    (totalSpend / totalEmployeeContribution) * 100,
    100
  ); // Cap utilization percentage at 100% in case weird scenarios cause it to exceed.

  // Set initial fromDate in URL if not present
  if (!searchParams.get('fromDate')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('fromDate', formatDateOnly(initialStartDate));
      setSearchParams(newParams);
  }

  const setStartDateUtcWithFirstSecond = (date: Date | undefined) => {
      if (date) {
          const midnightUtc = new Date(
              Date.UTC(
                  date.getUTCFullYear(),
                  date.getUTCMonth(),
                  date.getUTCDate(),
                  0,
                  0,
                  0,
                  0
              )
          );
          setStartDate(midnightUtc);
          // Update URL parameters with date only
          const newParams = new URLSearchParams(searchParams);
          newParams.set('fromDate', formatDateOnly(midnightUtc));
          setSearchParams(newParams);
      } else {
          setStartDate(getDefaultStartDate());
          // Remove fromDate from URL parameters
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('fromDate');
          setSearchParams(newParams);
      }
  };

  const setEndDateUtcWithLastSecond = (date: Date | undefined) => {
      if (date) {
          const lastSecondUtc = new Date(
              Date.UTC(
                  date.getUTCFullYear(),
                  date.getUTCMonth(),
                  date.getUTCDate(),
                  23,
                  59,
                  59,
                  999
              )
          );
          setEndDate(lastSecondUtc);
          // Update URL parameters with date only
          const newParams = new URLSearchParams(searchParams);
          newParams.set('toDate', formatDateOnly(lastSecondUtc));
          setSearchParams(newParams);
      } else {
          setEndDate(undefined);
          // Remove toDate from URL parameters
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('toDate');
          setSearchParams(newParams);
      }
  };

  const handleSearch = (term: string) => {
      setSearchTerm(term);
      const newParams = new URLSearchParams(searchParams);
      if (term) {
          newParams.set('searchTerm', term);
      } else {
          newParams.delete('searchTerm');
      }
      setSearchParams(newParams);
  };

  const handlePlansChange = (planIds: string[]) => {
      setSelectedPlans(planIds);
      const newParams = new URLSearchParams(searchParams);
      if (planIds.length > 0) {
          newParams.set('planId', planIds.join(','));
      } else {
          newParams.delete('planId');
      }
      setSearchParams(newParams);
  };

  const handleAccountsChange = (accountIds: string[]) => {
      setSelectedAccounts(accountIds);
      const newParams = new URLSearchParams(searchParams);
      if (accountIds.length > 0) {
          newParams.set('accountId', accountIds.join(','));
      } else {
          newParams.delete('accountId');
      }
      setSearchParams(newParams);
  };

  // Get unique plan IDs from employee's transactions
  const employeePlanIds = new Set<string>();
  transactions.data?.items.forEach(transaction => {
      transaction.charges?.forEach(charge => {
          if (charge.planId) {
              employeePlanIds.add(charge.planId);
          }
      });
  });

  // Filter plans to only those the employee has transactions in
  const employeePlans = allPlans.data?.items.filter(
      plan => employeePlanIds.has(plan.id)
  ) ?? [];

  // Get unique account IDs from employee's transactions
  const employeeAccountIds = new Set<string>();
  transactions.data?.items.forEach(transaction => {
      if (transaction.accountId) {
          employeeAccountIds.add(transaction.accountId);
      }
  });

  // Filter accounts to only those the employee has transactions in
  const employeeTransactionAccounts = accounts.data?.items.filter(
      account => employeeAccountIds.has(account.id)
  ) ?? [];

  const handleDownload = async () => {
      await exportTransactions.refetch();
      if (exportTransactions.data) {
          const csv = convertTransactionsToCsv(
              exportTransactions.data,
              false,
              serviceSectors.data?.items,
              allEmployees.data?.items,
              tagCategories.data?.items,
              allPlans.data?.items,
              accounts.data?.items
          );
          downloadCsv(csv, `Transactions-${new Date().toLocaleString()}`);
      }
  };

  const handleTaxLabFbtExtract = async () => {
      await exportTransactions.refetch();
      if (exportTransactions.data) {
          const csv = convertTransactionsToTaxLabFbtCsv(exportTransactions.data, allEmployees.data?.items);
          downloadCsv(csv, `Transactions-TaxLab-${new Date().toLocaleString()}`);
      }
  };

  return (
    <>
      <Box display={"flex"} flexDirection={"row"} gap={3} marginY={4}>
        <TotalDisplayCard>
          <StatisticTitle>Spend Tracking</StatisticTitle>
          <StatisticDisplay>{formatCurrency(totalSpend)}</StatisticDisplay>
          <StatisticSubtitle>{totalTxns} Transactions</StatisticSubtitle>
        </TotalDisplayCard>
        <TotalDisplayCard>
          <StatisticTitle>Balance Remaining</StatisticTitle>
          <StatisticDisplay>
            {formatCurrency(remainingBalance)}
          </StatisticDisplay>
          <StatisticSubtitle>
            {`${Math.round(100 - utilizationPercentage)}%`}
          </StatisticSubtitle>
        </TotalDisplayCard>
        <TotalDisplayCard>
          <StatisticTitle>Average Transaction</StatisticTitle>
          <StatisticDisplay>{formatCurrency(averageTxn || 0)}</StatisticDisplay>
        </TotalDisplayCard>
        <TotalDisplayCard>
          <StatisticTitle>Contribution Utilization</StatisticTitle>
          <Box
            display={"flex"}
            flexDirection={"row"}
            marginTop={1}
            alignItems={"center"}
          >
            <CircularProgress
              variant="determinate"
              value={utilizationPercentage}
              size={40}
              thickness={8}
              color="primary"
              sx={{ marginRight: 2 }}
            />

            <StatisticDisplay>
              {`${Math.round(utilizationPercentage || 0)}%`}
            </StatisticDisplay>
          </Box>
        </TotalDisplayCard>
      </Box>

      <TransactionsTableControlBar
          startDate={startDate}
          endDate={endDate}
          updateStartDate={setStartDateUtcWithFirstSecond}
          updateEndDate={setEndDateUtcWithLastSecond}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          plans={employeePlans}
          selectedPlans={selectedPlans}
          onPlansChange={handlePlansChange}
          accounts={employeeTransactionAccounts}
          selectedAccounts={selectedAccounts}
          onAccountsChange={handleAccountsChange}
          onExportCSV={handleDownload}
          onExportTaxLabFbt={handleTaxLabFbtExtract}
      />
      <TransactionsTable 
          transactions={transactions.data?.items || []}
          totalItems={transactions.data?.totalItems || 0}
          page={page}
          pageSize={pageSize}
          updatePage={setPage}
          updatePageSize={setPageSize}
          isLoading={transactions.isLoading}
          employees={allEmployees.data?.items}
          accounts={accounts.data?.items}
          serviceSectors={serviceSectors.data?.items}
      />
    </>
  );
};

export { EmployeeOverview };
